import type { Workgroup } from '@easyflow/shared';

const TOKEN_STORAGE_KEY = 'edgvoip_tenant_token';

export const WORKGROUP_UPDATED_EVENT = 'edgvoip:workgroup-updated';

export interface WorkgroupUpdatedDetail {
  workgroup: Workgroup;
}

/**
 * URL `<img src>` per l'avatar di un workgroup.
 *
 * Cache-buster `v` combina `avatar_url` (la R2 key contiene Date.now() unico
 * per upload) e `updated_at`: cambia sempre quando il backend persiste una
 * nuova immagine, anche se uno dei due campi non venisse rinfrescato.
 *
 * Il token JWT è passato in query string perché `<img>` non invia
 * l'Authorization header; il middleware backend accetta `?token=` solo su
 * questo endpoint asset GET (allowlist esplicita).
 */
export function workgroupAvatarSrc(wg: Pick<Workgroup, 'id' | 'avatar_url' | 'updated_at'>): string | undefined {
  if (!wg.avatar_url) return undefined;
  const v = encodeURIComponent(`${wg.updated_at ?? ''}|${wg.avatar_url}`);
  const tok = typeof localStorage !== 'undefined' ? localStorage.getItem(TOKEN_STORAGE_KEY) : null;
  const t = tok ? `&token=${encodeURIComponent(tok)}` : '';
  return `/api/messenger/workgroups/${wg.id}/avatar/file?v=${v}${t}`;
}

/**
 * URL `<img src>` per la cover image di un workgroup. Stessa logica di
 * cache-buster + token query dell'avatar (vedi `workgroupAvatarSrc`).
 */
export function workgroupCoverSrc(wg: Pick<Workgroup, 'id' | 'cover_image_url' | 'updated_at'>): string | undefined {
  if (!wg.cover_image_url) return undefined;
  const v = encodeURIComponent(`${wg.updated_at ?? ''}|${wg.cover_image_url}`);
  const tok = typeof localStorage !== 'undefined' ? localStorage.getItem(TOKEN_STORAGE_KEY) : null;
  const t = tok ? `&token=${encodeURIComponent(tok)}` : '';
  return `/api/messenger/workgroups/${wg.id}/cover/file?v=${v}${t}`;
}

/** Notifica tutte le superfici (lista, header, sidebar) che un workgroup è cambiato. */
export function emitWorkgroupUpdated(workgroup: Workgroup): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<WorkgroupUpdatedDetail>(WORKGROUP_UPDATED_EVENT, {
    detail: { workgroup },
  }));
}
